import React, { createContext, useCallback, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from '../i18nconfig';
import { SUPPORTED_LANGUAGES } from './languageConfig';

interface RouteTransitionContextType {
  isTransitioning: boolean;
  startTransition: (to: string) => void;
  endTransition: () => void;
  navigateWithTransition: (to: string) => void;
  navigateLocaleWithTransition: (to: string) => void;
}

const RouteTransitionContext = createContext<RouteTransitionContextType | undefined>(undefined);

export const RouteTransitionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const startTransition = useCallback((to: string) => {
    setIsTransitioning(true);
  }, []);

  const endTransition = useCallback(() => {
    setIsTransitioning(false);
  }, []);

  const navigateWithTransition = useCallback(
    (to: string) => {
      // Don't trigger transition for the same route
      if (to === location.pathname) {
        return;
      }

      startTransition(to);
      navigate(to);

      // Safety timeout to ensure we don't get stuck in loading state
      setTimeout(() => {
        endTransition();
      }, 1500);
    },
    [location.pathname, navigate, startTransition, endTransition],
  );

  const navigateLocaleWithTransition = useCallback(
    (to: string) => {
      // Get current locale, fallback to 'en' if not supported
      const currentLocale = i18n?.language && SUPPORTED_LANGUAGES.includes(i18n.language as any) ? i18n.language : 'en';

      let path = to.startsWith('/') ? to : `/${to}`;

      // Split path into segments to check for existing locale
      const pathSegments = path.split('/').filter((segment) => segment !== '');

      // Check if the first segment is a supported language
      const hasLocalePrefix = pathSegments.length > 0 && SUPPORTED_LANGUAGES.includes(pathSegments[0] as any);
      const hasUnsupportedLocalePrefix =
        pathSegments.length > 0 &&
        pathSegments[0].length === 2 &&
        !SUPPORTED_LANGUAGES.includes(pathSegments[0] as any);

      if (hasUnsupportedLocalePrefix) {
        // Remove unsupported locale and use current locale
        pathSegments.shift(); // Remove the unsupported locale
        path = pathSegments.length > 0 ? '/' + pathSegments.join('/') : '/';

        // Add current locale
        if (path === '/') {
          path = `/${currentLocale}`;
        } else {
          path = `/${currentLocale}${path}`;
        }
      } else if (hasLocalePrefix) {
        // If path already has a locale and it's the same as current, use as-is
        if (pathSegments[0] === currentLocale) {
          navigateWithTransition(path);
          return;
        }
        // If path has a different locale, replace it with current locale
        pathSegments[0] = currentLocale;
        path = '/' + pathSegments.join('/');
      } else {
        // Only add locale if not already present
        if (path === '/') {
          path = `/${currentLocale}`;
        } else {
          path = `/${currentLocale}${path}`;
        }
      }

      // Force language change before navigation if needed
      if (i18n.language !== currentLocale) {
        i18n.changeLanguage(currentLocale);
      }

      navigateWithTransition(path);
    },
    [navigateWithTransition],
  );

  return (
    <RouteTransitionContext.Provider
      value={{
        isTransitioning,
        startTransition,
        endTransition,
        navigateWithTransition,
        navigateLocaleWithTransition,
      }}
    >
      {children}
    </RouteTransitionContext.Provider>
  );
};

export const useRouteTransition = () => {
  const context = useContext(RouteTransitionContext);
  if (context === undefined) {
    throw new Error('useRouteTransition must be used within a RouteTransitionProvider');
  }
  return context;
};
